import React, { useContext } from 'react'
import PropTypes from 'prop-types'
import { DateTime } from 'luxon'
import { Link } from 'react-router-dom'

import {
  Box, Button, Checkbox, Image, ListItemWithImage, StatusBadge, Tag, Text,
} from '@campaignhub/suit-theme'

import 'react-tippy/dist/tippy.css'
import { Tooltip } from 'react-tippy'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faExternalLink, faFileAlt, faFileImage, faFilePdf, faFileVideo, faInfoCircle,
} from '@fortawesome/pro-light-svg-icons'

import PageContext from '@contexts/pageContext'
import useBlobMetadata from '@hooks/useBlobMetadata'
import customMetaData from '@functions/customMetaData'
import { cssBackgroundUrl, itemPreviewSource, itemThumbnailUrl } from '@functions/previewSource'

import Icons from '@components/Icons'

// The service-group icons the other tabs fall back on need entities.serviceGroups, which only the
// OnSite and Studio sections load — so key the placeholder off the file type instead.
const placeholderIcon = (fileTypeName) => {
  switch (fileTypeName?.toLowerCase()) {
    case 'pdf':
      return faFilePdf
    case 'mp4':
    case 'mov':
      return faFileVideo
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'dng':
    case 'cr2':
    case 'arw':
      return faFileImage
    default:
      return faFileAlt
  }
}

const metaValue = (customMetaValues, key) => customMetaValues[Object.keys(customMetaValues).find(x => x.includes(key))]

const ListItem = (props) => {
  const { callbacks: { togglePreview, toggleSelected }, item, selected } = props
  const {
    createdAt, directUrl, downloadUrl, fileTypeName, hasPreview, isAiGenerated, link, name, previewUrl, serviceName, typeLabel,
  } = item

  const { currentUser } = useContext(PageContext)

  // URL-only assets (e.g. isVirtualTour tours) have no downloadable file, so the thumbnail opens the
  // pasted tour link instead — while the row stays out of selection/download.
  const rowUrl = downloadUrl || directUrl

  // Anything with a generated preview, or an original the browser can render, opens the in-app
  // full-screen view instead of the raw file.
  const hasPreviewImage = !!itemPreviewSource({ hasPreview, previewUrl, fileTypeName, downloadUrl })
  const thumbnailUrl = itemThumbnailUrl({ hasPreview, previewUrl, fileTypeName, downloadUrl })

  // Shares the HEAD request the AI lookup already made for this blob. A blob can carry metadata
  // that isn't in customMetaData (ai_request_id, for one), so the entries decide whether there's
  // anything to show — the raw key count would put an empty tooltip on an AI asset.
  const customMetaValues = useBlobMetadata(downloadUrl)
  const metaEntries = Object.entries(customMetaData)
    .map(([key, label]) => ({ key, label, value: metaValue(customMetaValues, key) }))
    .filter(entry => entry.value)

  return (
    <ListItemWithImage imageComponent={(
      <Box
        alignItems="center"
        as={hasPreviewImage ? null : (rowUrl ? 'a' : null)}
        borderRight={['none', '1px solid']}
        borderBottom={['1px solid', 'none']}
        borderColor={['lineColor', 'lineColor']}
        href={hasPreviewImage ? undefined : rowUrl}
        justifyContent="center"
        onClick={hasPreviewImage ? () => togglePreview() : undefined}
        padding="medium"
        rel={!hasPreviewImage && !downloadUrl && directUrl ? 'noreferrer' : null}
        style={{ cursor: hasPreviewImage ? 'pointer' : 'auto', textDecoration: 'none' }}
        width={['100%', 'fit-content']}
        target={hasPreviewImage ? undefined : '_blank'}
      >
        <Image
          backgroundPosition="center center"
          boxProps={{
            backgroundColor: 'hoverLightGrey',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'contain',
          }}
          borderRadius={5}
          height={[180, '100%']}
          url={cssBackgroundUrl(thumbnailUrl)}
          width={['100%', 200]}
        >
          {!thumbnailUrl && (
            <Box
              alignItems="center"
              color="bodyFontLightColor"
              display="grid"
              justifyContent="center"
              width="100%"
            >
              {directUrl
                ? <Icons name="VirtualTour" size={36} />
                : <FontAwesomeIcon icon={placeholderIcon(fileTypeName)} size="3x" />}
            </Box>
          )}
        </Image>
      </Box>
    )}
    >
      <ListItemWithImage.Header>
        <ListItemWithImage.HeaderLeft>
          <ListItemWithImage.HeaderText>
            {name}
          </ListItemWithImage.HeaderText>
          {createdAt && (
            <Text color="bodyFontLightColor" fontSize="xsmall">
              Date Uploaded: {DateTime.fromISO(createdAt, { zone: currentUser.timeZone }).toFormat('LL/dd/yyyy')}
            </Text>
          )}
        </ListItemWithImage.HeaderLeft>
        {downloadUrl && (
          <ListItemWithImage.HeaderRight>
            <Checkbox checked={selected} marginRight={0} onClick={() => toggleSelected()} />
          </ListItemWithImage.HeaderRight>
        )}
      </ListItemWithImage.Header>

      <ListItemWithImage.Footer>
        <ListItemWithImage.FooterLeft boxProps={{ maxWidth: '75%!important' }}>
          <Box fontSize="small" marginBottom="small" alignItems="center">
            <StatusBadge
              color="mysteryGrey"
              text={serviceName}
              boxProps={{ marginRight: 'small' }}
            />
            {typeLabel && (
              <StatusBadge
                color="mysteryGrey"
                text={typeLabel}
                boxProps={{ marginRight: 'small' }}
              />
            )}
            {fileTypeName && fileTypeName !== typeLabel && (
              <StatusBadge
                color="mysteryGrey"
                text={fileTypeName}
                boxProps={{ marginRight: 'small' }}
              />
            )}
            {isAiGenerated && (
              <Tag boxProps={{ width: 'fit-content', fontSize: 'xsmall', marginRight: 'medium' }}>AI</Tag>
            )}
            {metaEntries.length ? (
              <Tooltip
                html={(
                  <div style={{ textAlign: 'left' }}>
                    {metaEntries.map(({ key, label, value }) => (
                      <Text fontSize="small" key={key}>
                        {label}: {key === 'datetimeoriginal'
                          ? DateTime.fromFormat(value, 'yyyy:MM:dd HH:mm:ss').toFormat('MM/dd/yy HH:mm')
                          : key === 'size'
                            ? `${(value / (1024 * 1024)).toFixed(2)} MB`
                            : value}
                      </Text>
                    ))}
                  </div>
                )}
                trigger="mouseenter"
                arrow
                arrowSize="regular"
                duration={0}
              >
                <FontAwesomeIcon icon={faInfoCircle} color="black" style={{ verticalAlign: 'middle' }} />
              </Tooltip>
            ) : null}
          </Box>
        </ListItemWithImage.FooterLeft>
        <ListItemWithImage.FooterRight>
          <Link to={link} style={{ textDecoration: 'none' }}>
            <Button
              buttonStyle="secondaryUtility"
              icon={<FontAwesomeIcon icon={faExternalLink} />}
              size="medium"
              height={37}
            >
              View Job
            </Button>
          </Link>
        </ListItemWithImage.FooterRight>
      </ListItemWithImage.Footer>
    </ListItemWithImage>
  )
}

ListItem.propTypes = {
  callbacks: PropTypes.object.isRequired,
  item: PropTypes.object.isRequired,
  selected: PropTypes.bool,
}

export default ListItem
